/**
 * useNewLeadsCount — lekki licznik NOWYCH zapytań (status === 'new').
 *
 * ETAP 5.5: zasila pomarańczowy badge przy pozycji "Zapytania" w sidebarze.
 *
 * Zasada:
 *   - tylko frontend, BEZ zmian w backendzie/bazie,
 *   - korzysta z istniejącego endpointu GET /api/panel/leads/all,
 *   - zlicza wyłącznie leady ze statusem 'new',
 *   - cache 60s w sessionStorage, żeby nie floodować API przy nawigacji.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

const CACHE_KEY = 'apisai_new_leads_count';
const CACHE_TTL_MS = 60 * 1000;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function readCache(userId) {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj.userId !== userId) return null;
    if (Date.now() - obj.ts > CACHE_TTL_MS) return null;
    return obj.count;
  } catch { return null; }
}

function writeCache(userId, count) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ userId, ts: Date.now(), count }));
  } catch {}
}

export function useNewLeadsCount() {
  const { user } = useAuth() || {};
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user?.id) { setCount(0); return; }

    const cached = readCache(user.id);
    if (cached !== null) { setCount(cached); return; }

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/panel/leads/all`, { headers: getAuthHeaders() });
        const items = Array.isArray(res?.data) ? res.data : (res?.data?.items || []);
        const newCount = items.filter((l) => (l?.status || 'new') === 'new' && !l?.is_demo).length;
        if (!cancelled) {
          writeCache(user.id, newCount);
          setCount(newCount);
        }
      } catch {
        if (!cancelled) setCount(0);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id]);

  return count;
}
